import { useEffect, useState } from 'react'
import { api, type Warengruppe, type WgAbgleich } from '../api'
import { anzahl } from '../format'
import { Tipp } from '../komponenten/Hinweis'

/* Warengruppen pflegen.
 *
 * Von dieser Liste gibt es **keinen Export** — sie steht nur als
 * Ansicht im Kassen-Backoffice. Deshalb kein Dateifeld, sondern ein
 * Textfeld: markieren, Strg+C, Strg+V.
 *
 * Und deshalb **erst zeigen, dann schreiben**. Bei einer Datei mit
 * festen Spalten weiß man, was drinsteht; bei Text aus der
 * Zwischenablage weiß man es erst, wenn man hinsieht.
 *
 * Was das Steuerprofil nicht leistet: Speise oder Getränk. Gemessen an
 * den 38 Gruppen im Backoffice des ersten Kunden trägt „vollrate 19 %"
 * elf Getränke — AFG, Bier, Wein — **und Non Food**. Die Angabe steht
 * am Artikel; wo sie fehlt, legt sie hier ein Mensch fest.
 */

const ARTEN = [
  { wert: 'FOOD', name: 'Speise' },
  { wert: 'BEVERAGE', name: 'Getränk' },
  { wert: 'OTHER', name: 'Sonstiges' },
  { wert: 'UNKNOWN', name: 'offen' },
]

function Einfuegen({ fertig }: { fertig: () => void }) {
  const [text, setText] = useState('')
  const [erg, setErg] = useState<WgAbgleich | null>(null)
  const [fehler, setFehler] = useState<string | null>(null)
  const [laeuft, setLaeuft] = useState(false)

  const lauf = async (uebernehmen: boolean) => {
    setFehler(null); setLaeuft(true)
    try {
      const a = await api.verwaltung.einfuegen(text, uebernehmen)
      setErg(a)
      if (uebernehmen) { setText(''); fertig() }
    } catch (e) {
      setFehler(e instanceof Error ? e.message : String(e))
    } finally {
      setLaeuft(false)
    }
  }

  return (
    <div className="karte">
      <h3>Liste aus der Kasse einfügen</h3>
      <p className="dezent">
        Im Lightspeed-Backoffice unter <b>Warengruppen</b> die Tabelle
        markieren und kopieren, hier einfügen. <b>Beide Seiten</b> — das
        Backoffice zeigt 25 Zeilen, die Liste hat mehr. Es wird erst
        gezeigt, was verstanden wurde, und erst danach geschrieben.
      </p>

      <textarea className="einfuegefeld" rows={6} value={text}
                placeholder="Hier einfügen …"
                onChange={(e) => { setText(e.target.value); setErg(null) }} />

      <div className="knopfreihe">
        <button className="verweis" disabled={!text.trim() || laeuft}
                onClick={() => void lauf(false)}>Erst ansehen</button>
        <button disabled={!erg || laeuft || erg.uebernommen}
                onClick={() => void lauf(true)}>Übernehmen</button>
      </div>

      {fehler && <div className="fehler" role="alert">{fehler}</div>}

      {erg && (
        <div className="ladeergebnis">
          <b>
            {anzahl(erg.zahlen.gelesen)} Warengruppen gelesen
            {erg.uebernommen ? ' und übernommen' : ''}
          </b>
          <ul>
            <li>{anzahl(erg.zahlen.neu)} noch nicht im Bestand
              {erg.zahlen.neu > 0 && (
                <span className="dezent"> — {erg.neu.map((g) => g.name).join(', ')}</span>
              )}
            </li>
            <li>{anzahl(erg.zahlen.bekannt)} schon bekannt</li>
            {erg.zahlen.nicht_eingefuegt > 0 && (
              <li>
                <b>{anzahl(erg.zahlen.nicht_eingefuegt)} im Bestand, aber nicht
                  eingefügt</b> — {erg.nicht_eingefuegt.map((g) => g.name).join(', ')}
                <div className="dezent">
                  Vermutlich fehlt die zweite Seite. Es wird nichts
                  stillgelegt: An Warengruppen hängen Buchungen.
                </div>
              </li>
            )}
          </ul>
          {erg.uebersprungen.length > 0 && (
            <div className="dezent">
              Nicht erkannt: {erg.uebersprungen.slice(0, 5).join(' · ')}
              {erg.uebersprungen.length > 5
                && ` … (${erg.uebersprungen.length} Zeilen)`}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export function Warengruppen() {
  const [liste, setListe] = useState<Warengruppe[] | null>(null)
  const [fehler, setFehler] = useState<string | null>(null)

  const laden = () => {
    api.verwaltung.warengruppen()
      .then((d) => setListe(d.warengruppen))
      .catch((e) => setFehler(e.message))
  }
  useEffect(laden, [])

  const setzen = async (name: string, art: string) => {
    setFehler(null)
    try {
      await api.verwaltung.art(name, art)
      laden()
    } catch (e) {
      setFehler(e instanceof Error ? e.message : String(e))
    }
  }

  if (fehler && !liste) return <div className="fehler">{fehler}</div>
  if (!liste) return <div className="laedt">Wird geladen …</div>

  const offen = liste.filter((g) => g.kind === 'UNKNOWN' && g.artikel > 0)
  const doppelt = liste.filter((g) => g.zeilen > 1)

  return (
    <div className="backoffice">
      {fehler && <div className="fehler" role="alert">{fehler}</div>}

      <Einfuegen fertig={laden} />

      <div className="karte">
        <div className="karte-kopf">
          <h3>Warengruppen</h3>
          <span className="dezent">{anzahl(liste.length)}</span>
        </div>

        {offen.length > 0 && (
          <p className="dezent">
            Bei <b>{anzahl(offen.length)}</b> ist offen, ob sie Speise oder
            Getränk sind. Das Steuerprofil beantwortet das <b>nicht</b> —
            unter „vollrate 19 %" stehen AFG und Non Food. Wo die
            Herleitung aus den Artikeln nichts findet, entscheidet es
            hier ein Mensch; der nächste Import überschreibt das nicht.
          </p>
        )}

        {doppelt.length > 0 && (
          <p className="dezent">
            <b>{anzahl(doppelt.length)}</b> Warengruppen stehen in der
            Datenbank doppelt — einmal mit dem Zahlencode aus dem Archiv,
            einmal mit dem Namen als Code aus der Artikelliste. Das
            passiert, wenn die Artikelliste vor dem Archiv gelesen wird,
            und genau so macht es der Ordnerimport. Auf den Kennzahlen
            liegt das nicht; was hier festgelegt wird, gilt für beide
            Zeilen.
          </p>
        )}

        <table>
          <thead>
            <tr>
              <th>Warengruppe</th><th>Steuerprofil</th>
              <th className="r">Artikel</th><th>Speise oder Getränk</th>
              <th>Herkunft</th>
            </tr>
          </thead>
          <tbody>
            {liste.map((g) => (
              <tr key={g.id}>
                <td>
                  <b>{g.name}</b>
                  <div className="dezent zahl">
                    {g.code}
                    {g.zeilen > 1 && (
                      <Tipp text="Zwei Datensätze für eine Warengruppe" fokus>
                        <span>{' '}· {g.zeilen} Zeilen</span>
                      </Tipp>
                    )}
                  </div>
                </td>
                <td className="dezent">
                  {g.steuerprofil ?? '—'}
                  {g.steuersatz !== null && (
                    <span className="zahl"> · {Number(g.steuersatz)} %</span>
                  )}
                </td>
                <td className="r zahl">{anzahl(g.artikel)}</td>
                <td>
                  <select value={g.kind}
                          onChange={(e) => void setzen(g.name, e.target.value)}>
                    {ARTEN.map((a) => (
                      <option key={a.wert} value={a.wert}>{a.name}</option>
                    ))}
                  </select>
                  {g.art_quelle === 'KUNDE' && (
                    <Tipp text="Festgelegt — der nächste Import ändert das nicht" fokus>
                      <span className="zustand">festgelegt</span>
                    </Tipp>
                  )}
                </td>
                <td className="dezent">
                  {g.herkunft === 'BACKOFFICE' ? 'von Hand' : 'aus der Kasse'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
